import { ref } from "vue";
import { storeToRefs, type StoreGeneric } from "pinia";
import { FilterContext, useFiltersStore } from "@/_store/filters.module";
import type { Pagination } from "@/types"; // Assuming types are available
import isEqual from "lodash/isEqual";
import type {
  DeviceListItem,
  DeviceOsOrClientVersions,
  DeviceStatusFilterItem,
} from "@/_store/devices.module.ts";
import { useFilters } from "@/composables/useFilters.ts";
import type { AgentHealthIssue } from "@/constants/devices.ts";

interface IDeviceStore<TDeviceItem extends DeviceListItem = DeviceListItem> extends StoreGeneric {
  $state: {
    devices: TDeviceItem[];
    totalDevices: number;
    pagination: Pagination;
    loading: boolean;
    showSkeletonLoader: boolean;
    osVersions: DeviceOsOrClientVersions;
    clientVersions: DeviceOsOrClientVersions;
    statuses: DeviceStatusFilterItem[];
  };

  // Methods
  init: () => Promise<void>;
  getDevices: (showLoader?: boolean) => Promise<void>;
  resetPagination: () => void;
  setPagination: (pagination: Pagination) => void;
  $reset: () => void;
}

interface DevicePageProps {
  hasVulnerabilities?: string;
  search?: string;
  widget?: string;
  agentHealth?: string;
}

export function useDevicePage<
  TDeviceItem extends DeviceListItem,
  FContext extends FilterContext.DEVICES | FilterContext.MSP_DEVICES = FilterContext.DEVICES,
>(
  store: IDeviceStore<TDeviceItem>,
  filterContext: FContext,
  props: Readonly<DevicePageProps>,
  // Callback for when filters update, since the component needs to clear selection
  options: { clearSelectionCallback: () => void }
) {
  const preview = ref<TDeviceItem | null>(null);
  const filtersStore = useFiltersStore();

  const {
    loading,
    devices,
    totalDevices,
    pagination,
    showSkeletonLoader,
    osVersions,
    clientVersions,
    statuses,
  } = storeToRefs(store);

  const { localFilters, showClearFiltersButton, clearFilters } = useFilters(
    {
      context: filterContext,
      searchKey: "search",
      onFilterChange: async () => {
        store.resetPagination();
        await store.getDevices();
        options.clearSelectionCallback();
      },
    },
    { deep: true, immediate: true }
  );

  // --- Shared Handlers ---

  const onPreviewUpdate = (item: TDeviceItem | null) => {
    preview.value = item ? { ...item } : null;
  };

  const onPageChange = async (newPagination: Pagination) => {
    store.setPagination(newPagination);
    await store.getDevices();
  };

  const clearFiltersCallback = async () => {
    await store.getDevices();
  };

  // --- Shared Lifecycle Hooks ---
  // We wrap these in functions to be called by the component's own lifecycle hooks.
  // This allows components to add their own logic *in addition* to this base logic.

  /**
   * Route props overlay the filters already in the store, they never reset them.
   *
   * Callers such as the MSP dashboard widgets seed the store and then push the route
   * without query params, so a key that is absent from the route means "keep what the
   * store holds", not "clear it". Overriding unconditionally used to wipe the
   * drill-down filter right after the first fetch had already rendered it.
   *
   * The guards are truthiness checks on purpose: vue-router hands us `null` for `?key`
   * and `""` for `?key=`, and neither should be read as an explicit `false`.
   */
  const onBaseMounted = async () => {
    const filtersWithQuery = {
      ...localFilters.value,
      search: props.search ? props.search : localFilters.value.search,
      ...(props.hasVulnerabilities && {
        hasVulnerabilities: props.hasVulnerabilities === "true",
      }),
      ...(props.widget && { widget: props.widget }),
      ...(props.agentHealth && {
        agentHealth: props.agentHealth.split(",") as AgentHealthIssue[],
      }),
    };
    await store.init();
    if (!isEqual(filtersWithQuery, localFilters.value)) {
      localFilters.value = filtersWithQuery;
    } else {
      await store.getDevices(true);
    }
  };

  const onBaseUnmounted = () => {
    store.$reset();
    filtersStore.resetFilters(filterContext);
  };

  // --- Return all shared state and handlers ---
  return {
    // State
    preview,
    localFilters,
    showClearFiltersButton,
    loading,
    devices,
    totalDevices,
    pagination,
    showSkeletonLoader,
    osVersions,
    clientVersions,
    statuses,

    // Handlers
    clearFilters,
    onPreviewUpdate,
    onPageChange,
    clearFiltersCallback,
    onBaseMounted,
    onBaseUnmounted,
  };
}
